import React, { useContext, useState } from 'react'
import PropTypes from 'prop-types'
import { Redirect } from 'react-router-dom'
import { mdiCheck } from '@mdi/js'

import { Button, Row, Col, Modal } from 'react-bootstrap'
import config from '../config'
import { getOrcidUrl } from '../lib/orcid'
import { userFullName } from '../lib/user'
import { OrcidIdLink, OrcidPermission } from '../components'
import { ApplicationContext } from './App'
import { Icon, IconLabel } from '@jcu/cookbook-react'
import { Helmet } from 'react-helmet'

const Settings = ({ unlinkOrcid }) => {
  const { user, orcid } = useContext(ApplicationContext)

  const [showConfirmationModal, setShowConfirmationModal] = useState(false)

  const fullName = userFullName(user)

  if (!user || !user.sub || !orcid || !orcid.orcid_id) {
    return <Redirect to="/" />
  }

  return (
    <>
      <Helmet>
        <title>Settings</title>
      </Helmet>
      <Row>
        <Col>
          <h1>{config.app.name} Settings</h1>
          <IconLabel className="align-items-center my-2 mb-4">
            <IconLabel.Icon className="pe-1">
              <Icon
                style={{ verticalAlign: 'middle' }}
                path={mdiCheck}
                size="2.5em"
                title="Linked!"
                color="008a00"
              />
            </IconLabel.Icon>
            <IconLabel.Content className="lead">
              Your JCU ID ({fullName ? `${fullName}, ` : ''}
              {user.sub}) is successfully linked to
              <br />
              <OrcidIdLink orcidId={orcid.orcid_id} sandbox={orcid.sandbox} />
            </IconLabel.Content>
          </IconLabel>

          <div className="mb-3">
            <h2>Additional Permissions</h2>
            <p className="small text-muted">
              These are other optional actions that you can allow applications
              at James Cook University to perform on your behalf. Note that
              permission usage can change over time.
            </p>
            <ul className="list-group list-group-flush">
              <OrcidPermission
                scope="/read-limited"
                tokens={orcid.tokens}
                unlinkOrcid={unlinkOrcid}
              />
              <OrcidPermission
                scope="/person/update"
                tokens={orcid.tokens}
                unlinkOrcid={unlinkOrcid}
              />
              <OrcidPermission
                scope="/activities/update"
                tokens={orcid.tokens}
                unlinkOrcid={unlinkOrcid}
              />
            </ul>
          </div>

          <div className="mb-3">
            <h2>Data Sharing</h2>
            <p className="small text-muted">
              Once you have granted specific permissions, you can use these
              options to send data to your ORCID record. Keep in mind that data
              will use your default <em>Privacy Preferences</em> to determine
              who can see this data.{' '}
              <a
                href={getOrcidUrl('/account', orcid.sandbox)}
                target="_blank"
                rel="noreferrer noopener"
              >
                Set this now
              </a>
            </p>
            <p>
              Coming soon - we&apos;ll provide options for sharing data shortly.
            </p>
          </div>

          <h2>Danger Zone</h2>
          <p className="small text-muted">
            If your ORCID iD is linked incorrectly, you can unlink it here. You
            will need to relink your ORCID iD again and this will affect all
            permissions and JCU applications that require them.
          </p>
          <div className="d-grid d-sm-block mb-3">
            <Button
              variant="danger"
              className="text-wrap px-4"
              onClick={event => {
                event.preventDefault()
                setShowConfirmationModal(true)
              }}
            >
              Unlink my ORCID iD
            </Button>
          </div>
        </Col>
      </Row>

      <Modal
        show={showConfirmationModal}
        onHide={() => setShowConfirmationModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Unlink My ORCID iD</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>You are about to unlink your ORCID iD from your JCU account.</p>
          <p>Are you sure you want to continue?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowConfirmationModal(false)}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={() => unlinkOrcid()}>
            Unlink
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

Settings.propTypes = {
  unlinkOrcid: PropTypes.func.isRequired,
}

export default Settings
